/*
  Medición del probador virtual (snippets/size-advisor.liquid) y de la guía de
  tallas (snippets/size_guide.liquid).

  - Empuja eventos al dataLayer (GTM -> GA4): size_advisor_*, size_guide_open y
    size_tools_add_to_cart, este último en TODOS los add-to-cart de <product-form>
    con el origen de la talla, para comparar quienes usaron el probador vs quienes no.
  - Marca la línea del carrito con propiedades ocultas (el "_" las esconde en el
    carrito y el checkout, pero quedan en el pedido) cuando el probador influyó:
      _recomendador: modal | post_uso | auto
      _talla_recomendada: talla que entregó el probador
    Así se cruzan compras y cambios/devoluciones desde los pedidos de Shopify.

  El estado de uso vive en sessionStorage por producto: el probador se puede usar,
  cerrar y agregar al carro después con el botón normal de la ficha.

  Orígenes del add-to-cart:
  - modal: botón "Agregar al carrito" del propio probador.
  - post_uso: botón normal, después de ver una recomendación para ese producto.
  - auto: botón normal, con la talla que el perfil guardado aplicó sola (sin abrir
    el probador) y que el usuario no cambió.
  - sin_uso: nada de lo anterior.
*/
(() => {
  if (window.SizeTracking) return;

  const STATE_KEY = 'pinflag_size_tracking_v1';
  const ORIGIN_PROPERTY = 'properties[_recomendador]';
  const SIZE_PROPERTY = 'properties[_talla_recomendada]';
  const MODAL_FLAG_TTL_MS = 5000;

  let modalAddToCart = null; // { sectionId, at } - lo marca el botón del probador
  let pendingAddToCart = null; // datos del último submit, se reportan si el add-to-cart funciona

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(STATE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function writeState(state) {
    try {
      sessionStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch (e) {
      // sessionStorage no disponible (modo privado) - la atribución dura solo esta vista.
    }
  }

  const memoryState = {};

  function getProductState(productId) {
    if (!productId) return {};
    return Object.assign({}, memoryState[productId], readState()[productId]);
  }

  function mark(productId, patch) {
    if (!productId) return;
    memoryState[productId] = Object.assign({}, memoryState[productId], patch);
    const state = readState();
    state[productId] = Object.assign({}, state[productId], patch);
    writeState(state);
  }

  function push(event, params) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, params));
  }

  function flagModalAddToCart(sectionId) {
    modalAddToCart = { sectionId, at: Date.now() };
  }

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  // Mismo criterio que getRadioLabelText() del probador: el texto corto ("3M")
  // vive en el <label>, no en el value del radio.
  function radioLabelText(input) {
    const label = document.querySelector('label[for="' + input.id + '"]');
    if (!label) return input.value;
    let text = '';
    label.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) text += node.textContent;
    });
    return text.trim() || input.value;
  }

  function selectedOptions(sectionId) {
    const scope = document.querySelector('variant-selects[data-section="' + sectionId + '"]');
    if (!scope) return [];
    const radios = Array.from(scope.querySelectorAll('input[type="radio"]:checked')).map(radioLabelText);
    const selects = Array.from(scope.querySelectorAll('select')).map((select) => select.value);
    return radios.concat(selects).filter(Boolean);
  }

  function advisorProductId(sectionId) {
    const el = document.querySelector(
      '[data-size-advisor-modal][data-section="' + sectionId + '"], [data-size-advisor-trigger][data-section="' + sectionId + '"]'
    );
    return el ? el.dataset.productId : null;
  }

  function setProperty(form, name, value) {
    let input = form.querySelector('input[name="' + name + '"]');
    if (!value) {
      if (input) input.disabled = true;
      return;
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.disabled = false;
    input.value = value;
  }

  // Captura: corre antes que el submit de <product-form> arme su FormData.
  function onSubmit(event) {
    const form = event.target;
    const productForm = form && form.closest ? form.closest('product-form') : null;
    if (!productForm) return;

    const sectionId = productForm.dataset.sectionId;
    const productId = advisorProductId(sectionId);
    const advisorAvailable = Boolean(productId);
    const state = getProductState(productId);
    const options = selectedOptions(sectionId).map(normalize);

    const fromModal =
      modalAddToCart && modalAddToCart.sectionId === sectionId && Date.now() - modalAddToCart.at < MODAL_FLAG_TTL_MS;
    modalAddToCart = null;

    let origin = 'sin_uso';
    if (fromModal) origin = 'modal';
    else if (state.used) origin = 'post_uso';
    else if (state.autoApplied && options.includes(normalize(state.autoApplied))) origin = 'auto';

    const recommendedSize = origin === 'sin_uso' ? '' : state.recommended || state.autoApplied || '';

    setProperty(form, ORIGIN_PROPERTY, origin === 'sin_uso' ? '' : origin);
    setProperty(form, SIZE_PROPERTY, recommendedSize);

    pendingAddToCart = {
      sectionId,
      productId,
      advisorAvailable,
      origin,
      recommendedSize,
      selected: selectedOptions(sectionId).join(' / '),
      matchesRecommendation: recommendedSize ? options.includes(normalize(recommendedSize)) : null,
    };
  }

  function onCartUpdate(event) {
    if (!event || event.source !== 'product-form' || !pendingAddToCart) return;
    const pending = pendingAddToCart;
    pendingAddToCart = null;
    const item = event.cartData || {};
    const productId = pending.productId || (item.product_id ? String(item.product_id) : null);
    const state = getProductState(productId);

    push('size_tools_add_to_cart', {
      product_id: productId,
      product_title: item.product_title || null,
      variant_id: item.variant_id || event.productVariantId || null,
      selected_options: pending.selected,
      advisor_available: pending.advisorAvailable,
      advisor_origin: pending.origin,
      advisor_used: pending.origin !== 'sin_uso',
      recommended_size: pending.recommendedSize || null,
      matches_recommendation: pending.matchesRecommendation,
      size_guide_opened: Boolean(state.sizeGuide),
    });
  }

  document.addEventListener('submit', onSubmit, true);

  document.addEventListener('DOMContentLoaded', () => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    subscribe(PUB_SUB_EVENTS.cartError, () => {
      pendingAddToCart = null;
    });
  });

  window.SizeTracking = { push, mark, getProductState, flagModalAddToCart };

  // Llamadas hechas antes de que cargara este archivo (ver track() en el probador).
  (window.SizeTrackingQueue || []).forEach((call) => window.SizeTracking[call[0]].apply(null, call[1]));
  window.SizeTrackingQueue = [];
})();
